/**
 * Deterministic OHLC envelope over an existing close walk (§1.5).
 *
 * No RNG and no fifth seed: open/high/low/volume are a pure function of the
 * closes the golden digest already covers. Local mode has no synthetic
 * corporate actions, so adjClose === close and cumSplit === 1.
 */
import type { OhlcSeries, Ticker, WindowKey } from '@meridian/contracts';

import {
  SYNTHETIC_OHLC_WICK_ALPHA,
  SYNTHETIC_VOLUME_BASE,
  SYNTHETIC_VOLUME_FLOOR,
  SYNTHETIC_VOLUME_GAMMA,
  SYNTHETIC_VOLUME_R_REF,
} from '../constants/technical';

export interface SyntheticOhlcLegs {
  readonly open: readonly number[];
  readonly high: readonly number[];
  readonly low: readonly number[];
  readonly close: readonly number[];
  readonly adjClose: readonly number[];
  readonly volume: readonly number[];
  readonly cumSplit: readonly number[];
}

function requirePositiveCloses(closes: readonly number[]): void {
  if (closes.length === 0) {
    throw new RangeError('syntheticOhlcFromCloses: closes must not be empty');
  }
  if (closes.some((close) => !Number.isFinite(close) || close <= 0)) {
    throw new RangeError('syntheticOhlcFromCloses: closes must be strictly positive and finite');
  }
}

function previousClose(closes: readonly number[], index: number): number | undefined {
  return index === 0 ? undefined : closes[index - 1];
}

function wickPad(close: number, previous: number | undefined): number {
  return SYNTHETIC_OHLC_WICK_ALPHA * (previous === undefined ? 0 : Math.abs(close - previous));
}

function volumeAt(current: number, previous: number | undefined): number {
  const relative = previous === undefined ? 0 : Math.abs(current - previous) / previous;
  const scaled =
    SYNTHETIC_VOLUME_BASE * (1 + (SYNTHETIC_VOLUME_GAMMA * relative) / SYNTHETIC_VOLUME_R_REF);
  return Math.max(SYNTHETIC_VOLUME_FLOOR, Math.round(scaled));
}

interface SyntheticBar {
  readonly open: number;
  readonly high: number;
  readonly low: number;
  readonly volume: number;
}

/**
 * One bar from a close and the close before it. `previous` is `undefined` only
 * at index 0, and that bar is flat by construction: `open === close`, and a
 * zero wick pad leaves `high === low === close`.
 */
function syntheticBar(close: number, previous: number | undefined): SyntheticBar {
  const open = previous ?? close;
  const pad = wickPad(close, previous);
  return {
    open,
    high: Math.max(open, close) + pad,
    low: Math.min(open, close) - pad,
    volume: volumeAt(close, previous),
  };
}

export function syntheticOhlcFromCloses(closes: readonly number[]): SyntheticOhlcLegs {
  requirePositiveCloses(closes);
  /* Every leg is derived per index from `closes` alone — no leg is read back
     out of another leg, so there is no cross-array index to guard. The one
     indexed read is `closes[index - 1]`, whose `undefined` at index 0 is the
     value `syntheticBar` wants rather than a mismatch to fall back from. */
  const bars = closes.map((close, index) => syntheticBar(close, previousClose(closes, index)));
  return {
    open: bars.map((bar) => bar.open),
    high: bars.map((bar) => bar.high),
    low: bars.map((bar) => bar.low),
    close: [...closes],
    adjClose: [...closes],
    volume: bars.map((bar) => bar.volume),
    cumSplit: closes.map(() => 1),
  };
}

export function toOhlcSeries(
  ticker: Ticker,
  window: WindowKey,
  sessions: readonly string[],
  legs: SyntheticOhlcLegs,
): OhlcSeries {
  const fromOn = sessions[0];
  const toOn = sessions[sessions.length - 1];
  if (fromOn === undefined || toOn === undefined) {
    throw new RangeError('toOhlcSeries: sessions must not be empty');
  }
  const n = sessions.length;
  if (
    legs.open.length !== n ||
    legs.high.length !== n ||
    legs.low.length !== n ||
    legs.close.length !== n ||
    legs.adjClose.length !== n ||
    legs.volume.length !== n ||
    legs.cumSplit.length !== n
  ) {
    throw new RangeError('toOhlcSeries: sessions and legs must have the same length');
  }
  return {
    ticker,
    window,
    sessions: [...sessions],
    open: [...legs.open],
    high: [...legs.high],
    low: [...legs.low],
    close: [...legs.close],
    adjClose: [...legs.adjClose],
    volume: [...legs.volume],
    cumSplit: [...legs.cumSplit],
    splitKnowledge: 'verified_none',
    volumeKnowledge: 'canonical',
    coverage: { fromOn, toOn, barCount: sessions.length, truncated: false },
  };
}
